// Small, dependency-free array helpers — the replacement for `Sugar.Array`.
//
// The file is in two halves, and the split matters. The pure helpers take
// `readonly` inputs and return a new array. The in-place ones mutate the array
// passed in and return that same array, because Sugar's equivalents did and call
// sites rely on it — several discarded the return value entirely and went on to
// read the array they had handed over. Each function's doc says which it is;
// picking the wrong one is a silent bug rather than a type error.

/** Direction for {@link arraySortBy}. */
export type SortDirection = 'asc' | 'desc';

export interface ArraySortByOptions<K> {
    /** Sort direction. Defaults to `'asc'`. */
    direction?: SortDirection;
    /**
     * Comparator for the selected keys, returning <0 / 0 / >0. Defaults to a
     * natural comparison (`<` / `>`), which is correct for numbers and strings
     * (including our lexicographically-sortable fractional-index keys).
     */
    compare?: (a: K, b: K) => number;
}

function naturalCompare<K>(a: K, b: K): number {
    if (a < b) {
        return -1;
    }
    if (a > b) {
        return 1;
    }
    return 0;
}

/**
 * Return a new array sorted by the key produced by `selector`.
 *
 * The sort is **stable**: elements whose selected keys compare equal keep their
 * original relative order. Stability is guaranteed here via an explicit original-
 * index tiebreak rather than relying on the engine's `Array.prototype.sort`.
 *
 * The input is never mutated (and may be `readonly`).
 */
export function arraySortBy<T, K>(
    items: readonly T[],
    selector: (item: T) => K,
    options: ArraySortByOptions<K> = {},
): T[] {
    const { direction = 'asc', compare = naturalCompare } = options;
    const sign = direction === 'desc' ? -1 : 1;

    return items
        .map((item, index) => ({ item, key: selector(item), index }))
        .sort((a, b) => {
            const c = compare(a.key, b.key);
            // Equal keys fall back to original index, preserving input order (stable).
            return c !== 0 ? sign * c : a.index - b.index;
        })
        .map((entry) => entry.item);
}

/**
 * Sort `items` **in place** by the selected key and return that same array.
 *
 * `Sugar.Array.sortBy` sorted in place, and a number of call sites discarded its
 * return value entirely and went on to read the array they had passed in. Those
 * sites need this; anything that uses the returned array should prefer the pure
 * {@link arraySortBy}.
 */
export function arraySortByInPlace<T, K>(
    items: T[],
    selector: (item: T) => K,
    options: ArraySortByOptions<K> = {},
): T[] {
    const sorted = arraySortBy(items, selector, options);
    for (let i = 0; i < sorted.length; i++) {
        items[i] = sorted[i];
    }
    return items;
}

/**
 * Return a new array containing every item for which `predicate` is falsy — i.e.
 * the complement of {@link Array.prototype.filter}. The input is never mutated
 * (and may be `readonly`).
 */
export function arrayExclude<T>(
    items: readonly T[],
    predicate: (item: T, index: number) => boolean,
): T[] {
    return items.filter((item, index) => !predicate(item, index));
}

// ---------------------------------------------------------------------------
// In-place helpers.
//
// Sugar's `remove`, `insert`, `append` and `removeAt` all mutated the array
// passed in and returned that same array. Call sites depend on the mutation —
// they hold a reference and expect it to change — so these keep that contract
// rather than quietly turning into pure functions. They return the same array
// so a `return arrayRemoveAt(...)` call site still reads correctly.
// ---------------------------------------------------------------------------

/** Remove **every** item matching `predicate`, in place. Returns the same array. */
export function arrayRemoveWhere<T>(items: T[], predicate: (item: T) => boolean): T[] {
    for (let i = items.length - 1; i >= 0; i--) {
        if (predicate(items[i])) {
            items.splice(i, 1);
        }
    }
    return items;
}

/**
 * Remove every occurrence of `value`, compared by identity, in place. Returns
 * the same array.
 *
 * Sugar compared plain objects *structurally* here, so a lookalike built
 * elsewhere would match. Every call site passes either a primitive, a class
 * instance (which Sugar already matched by identity), or an object it pulled
 * out of this very array, so identity is equivalent for all of them — and it
 * cannot over-remove a structurally identical neighbour the caller meant to keep.
 */
export function arrayRemoveValue<T>(items: T[], value: T): T[] {
    return arrayRemoveWhere(items, (item) => item === value);
}

/** Remove the item at `index`, in place. Out-of-range indexes are ignored. Returns the same array. */
export function arrayRemoveAt<T>(items: T[], index: number): T[] {
    if (index >= 0 && index < items.length) {
        items.splice(index, 1);
    }
    return items;
}

/** Insert `value` at `index`, in place. Returns the same array. */
export function arrayInsertAt<T>(items: T[], value: T, index: number): T[] {
    items.splice(index, 0, value);
    return items;
}

/**
 * Insert every element of `toAdd` at `index`, in place. Returns the same array.
 *
 * Sugar's `insert` took either a single item or an array and spread the latter,
 * so the two cases are split here to keep that ambiguity out of the type.
 */
export function arrayInsertAllAt<T>(items: T[], toAdd: readonly T[], index: number): T[] {
    items.splice(index, 0, ...toAdd);
    return items;
}

/**
 * Append every element of `toAdd` to `items`, in place. Returns the same array.
 *
 * Spread-free because these arrays can hold tens of thousands of entries and
 * `push(...huge)` overflows the call stack.
 */
export function arrayAppend<T>(items: T[], toAdd: readonly T[]): T[] {
    for (const item of toAdd) {
        items.push(item);
    }
    return items;
}

// ---------------------------------------------------------------------------
// Pure helpers.
// ---------------------------------------------------------------------------

/**
 * Distinct items, keeping the first occurrence of each and comparing by
 * identity. The input is never mutated.
 *
 * As with {@link arrayRemoveValue}, Sugar compared plain objects structurally.
 * Every call site holds primitives or class instances, which Sugar deduplicated
 * by identity anyway.
 */
export function arrayUnique<T>(items: readonly T[]): T[] {
    return [...new Set(items)];
}

/** Distinct items by selected key, keeping the first occurrence of each key. */
export function arrayUniqueBy<T, K>(items: readonly T[], selector: (item: T) => K): T[] {
    const seen = new Set<K>();
    const result: T[] = [];
    for (const item of items) {
        const key = selector(item);
        if (!seen.has(key)) {
            seen.add(key);
            result.push(item);
        }
    }
    return result;
}

/** Group items into a plain object keyed by the selected string. */
export function arrayGroupBy<T>(
    items: readonly T[],
    keySelector: (item: T) => string,
): Record<string, T[]> {
    const result: Record<string, T[]> = {};
    for (const item of items) {
        const key = keySelector(item);
        (result[key] ??= []).push(item);
    }
    return result;
}

/**
 * Whether two arrays hold the same items in the same order, compared by identity.
 *
 * Sugar's `isEqual` compared deeply. Both call sites migrated off it hold arrays
 * of strings, where the two are equivalent.
 */
export function arraysEqual<T>(a: readonly T[], b: readonly T[]): boolean {
    return a.length === b.length && a.every((value, index) => value === b[index]);
}

/** Total of the selected numbers. Zero for an empty array. */
export function arraySumBy<T>(items: readonly T[], selector: (item: T) => number): number {
    return items.reduce((total, item) => total + selector(item), 0);
}

/** Mean of the values, or `0` for an empty array — the value Sugar returned rather than `NaN`. */
export function arrayAverage(values: readonly number[]): number {
    return values.length === 0 ? 0 : values.reduce((total, v) => total + v, 0) / values.length;
}

/**
 * The **item** whose selected key is smallest, or `undefined` when empty. Ties
 * keep the earliest item. Note this returns the item, not the key.
 */
export function arrayMinBy<T, K>(items: readonly T[], selector: (item: T) => K): T | undefined {
    return pickBy(items, selector, (candidate, best) => candidate < best);
}

/**
 * The **item** whose selected key is largest, or `undefined` when empty. Ties
 * keep the earliest item. Note this returns the item, not the key.
 */
export function arrayMaxBy<T, K>(items: readonly T[], selector: (item: T) => K): T | undefined {
    return pickBy(items, selector, (candidate, best) => candidate > best);
}

function pickBy<T, K>(
    items: readonly T[],
    selector: (item: T) => K,
    isBetter: (candidate: K, best: K) => boolean,
): T | undefined {
    let best: T | undefined;
    let bestKey: K | undefined;
    // A separate flag rather than `bestKey === undefined`, so a selector that
    // legitimately returns undefined doesn't restart the search on every item.
    let found = false;
    for (const item of items) {
        const key = selector(item);
        // Strictly better only, so the earliest item wins a tie.
        if (!found || isBetter(key, bestKey as K)) {
            best = item;
            bestKey = key;
            found = true;
        }
    }
    return best;
}
